import { useFormikContext } from "formik";
import { IconInfoCircle } from "@tabler/icons-react";
import {
  Bank,
  CalendarCheck,
  Cctv,
  Charges,
  ConditionsParticulieres,
  DescriptionLogement,
  HousePlus,
  HousePrivatif,
  HouseTree,
  ImmeublePeople,
  Internet,
  Surface,
  Temperature,
  Water,
  // @ts-expect-error pure js
} from "../../UI/Icons";
// @ts-expect-error pure js
import TextInput from "../../UI/TextInput";
// @ts-expect-error pure js
import Select from "../../UI/Select";
// @ts-expect-error pure js
import ExpandableCheckboxList from "../../UI/ExpandableCheckboxList";
// @ts-expect-error pure js
import Radio from "../../UI/Radio";
// @ts-expect-error pure js
import StyledForm from "../../UI/StyledForm";
// @ts-expect-error pure js
import TextArea from "../../UI/TextArea";
// @ts-expect-error pure js
import useLeaseTemplates from "../../../hooks/use-lease-templates";
import Tooltip from "../../UI/Tooltip";
// @ts-expect-error pure js
import Checkbox from "../../UI/Checkbox";
import Card from "../../UI/Card";
import Callout from "../../UI/Callout";
import Title from "../../UI/Title";
import Text from "../../UI/Text";
import ClausesForm from "../../Tenant/Forms/ClausesForm";
// @ts-expect-error pure js
import SelectIban from "../../UI/SelectIban";
import DpePrefill from "./DpePrefill";
import styles from "./EquipementsLocal.module.css";

interface EquipementsLocalProps {
  edit?: boolean;
  isCreation?: boolean;
}

interface EquipementsLocalValues {
  type?: string;
  is_immeuble?: boolean;
  is_coloc?: boolean;
  type_habitat?: string;
  id_lease_template?: string | number;
  equipements: string[];
  technologies: string[];
  locaux_privatifs: string[];
  locaux_communs: string[];
  logement_espaces_communs: string[];
  logement_espaces_privatifs: string[];
  autres_pieces: string[];
  nb_rooms?: string | number;
  year_built?: string;
}

const EQUIPEMENTS = [
  "Interphone",
  "Digicode",
  "Eau froide collective",
  "cuisine équipée",
  "cuisine aménagée",
  "climatisation",
  "pompe à chaleur",
  "chaudière",
];

const TECHNOLOGIES = ["Adsl", "Fibre"];

const LOCAUX_PRIVATIFS = [
  "Parking couvert",
  "Parking aerien",
  "Box de stockage",
  "Garage fermé",
  "Jardin privatif",
  "Cour privative",
  "Cave",
  "Borne de recharge électrique",
];

const LOCAUX_COMMUNS = [
  "Ascenseur",
  "Jardin",
  "Espace vert",
  "Cour",
  "Garage à vélo",
];
const LOGEMENT_ESPACES_PRIVATIFS = [
  "Chambre",
  "Placard",
  "Place de parking",
  "Cave",
];
const LOGEMENT_ESPACES_COMMUNS = [
  "Salon",
  "Cuisine",
  "Toilettes",
  "Salle de bain",
  "Buanderie",
];

const AUTRES_PIECES = [
  "Entrée indépendante",
  "Salon",
  "Salon avec cuisine",
  "Salle de bain",
  "WC",
  "Chambre 1",
  "Chambre 2",
  "Chambre 3",
  "Balcon",
  "Terrasse",
  "Cuisine",
  "Pièce de vie unique (studio)",
];
const LOCAL_PIECES = [
  "Espace de vente",
  "Réserve",
  "Bureau",
  "WC",
  "Entrepôt",
  "Local technique",
  "Vestiaire",
];

export default function EquipementsLocal({
  edit,
  isCreation,
}: EquipementsLocalProps) {
  const { values, setFieldValue } = useFormikContext<EquipementsLocalValues>();
  const { leaseTemplates } = useLeaseTemplates();

  function addItem(fieldName: string, item: string) {
    const current = values[fieldName as keyof EquipementsLocalValues] as
      | string[]
      | undefined
      | null;
    if (current === undefined || current === null) {
      setFieldValue(fieldName, [item]);
    } else if (!current.includes(item)) {
      setFieldValue(fieldName, [...current, item]);
    }
  }

  const equipementsCheckboxItems = [
    ...EQUIPEMENTS,
    ...values.equipements.filter((i) => !EQUIPEMENTS.includes(i)),
  ];
  const technologiesCheckboxItems = [
    ...TECHNOLOGIES,
    ...values.technologies.filter((i) => !TECHNOLOGIES.includes(i)),
  ];
  const locauxPrivatifsCheckboxItems = [
    ...LOCAUX_PRIVATIFS,
    ...values.locaux_privatifs.filter((i) => !LOCAUX_PRIVATIFS.includes(i)),
  ];
  const locauxCommunsCheckboxItems = [
    ...LOCAUX_COMMUNS,
    ...values.locaux_communs.filter((i) => !LOCAUX_COMMUNS.includes(i)),
  ];
  const logementEspacesCommunsCheckboxItems = [
    ...LOGEMENT_ESPACES_COMMUNS,
    ...values.logement_espaces_communs.filter(
      (i) => !LOGEMENT_ESPACES_COMMUNS.includes(i),
    ),
  ];
  const logementEspacesPrivatifsCheckboxItems = [
    ...LOGEMENT_ESPACES_PRIVATIFS,
    ...values.logement_espaces_privatifs.filter(
      (i) => !LOGEMENT_ESPACES_PRIVATIFS.includes(i),
    ),
  ];
  const autresPiecesCheckboxItems = [
    ...AUTRES_PIECES,
    ...values.autres_pieces.filter((i) => !AUTRES_PIECES.includes(i)),
  ];
  const localPiecesCheckboxItems = [
    ...LOCAL_PIECES,
    ...values.autres_pieces.filter((i) => !LOCAL_PIECES.includes(i)),
  ];

  const leaseTemplate = leaseTemplates?.find(
    (lease: { id: number }) =>
      parseInt(values.id_lease_template as string) === lease.id,
  );

  // À la création le type de bail n'est plus saisi : la coloc à baux
  // individuels se détecte via nb_rooms (une chambre = un bien)
  const isColocIndividuelle =
    (leaseTemplate?.colocation && !leaseTemplate?.collectif) ||
    (values.is_coloc && parseInt(String(values.nb_rooms)) > 0);

  // Rendue en tête de formulaire (obligatoire) pour les locaux commerciaux,
  // dans la section optionnelle pour les autres types de bien
  const descriptionBailField = (
    <StyledForm
      formElement={
        <TextArea
          type="text"
          name="description"
          placeholder="Description détaillée intégrée au bail."
        />
      }
      label={
        <div>
          Description détaillée du bien figurant dans le bail{" "}
          <Tooltip content={"Cette description sera intégrée au bail."}>
            <IconInfoCircle size={16} />
          </Tooltip>
        </div>
      }
      icon={<DescriptionLogement />}
      mandatory={values.type === "commercial"}
    />
  );

  return (
    <div className={styles.fields}>
      {values.type === "habitation" && <DpePrefill />}
      {edit && (
        <>
          <StyledForm
            formElement={
              <Select name="type">
                <option value="habitation">
                  Local à usage d'habitation (Maison, appartement, chambre)
                </option>
                <option value="commercial">Local à usage commercial</option>
                <option value="garage">
                  Autre type de local (garage, parking, etc)
                </option>
              </Select>
            }
            label="Type de local"
            icon={<CalendarCheck stroke={"#9D9D9D"} />}
            mandatory
          />
          {values.type === "habitation" && (
            <>
              <StyledForm
                formElement={
                  <Checkbox name={"is_coloc"}>
                    Ce bien est en colocation
                  </Checkbox>
                }
                label="Mode d'exploitation du logement"
                icon={<HousePlus />}
                mandatory
              />
              <StyledForm
                formElement={
                  <div className={styles.gridCols}>
                    <Radio
                      name="type_habitat"
                      value="individuel"
                      label="Logement indépendant"
                    />
                    <Radio
                      name="type_habitat"
                      value="collectif"
                      label="Logement dans un immeuble"
                    />
                  </div>
                }
                label="Type d'habitat"
                icon={<HousePlus />}
                mandatory
              />
            </>
          )}
        </>
      )}
      {!values.is_immeuble && (
        <StyledForm
          formElement={
            <div className={styles.stack}>
              <div className={styles.row}>
                <TextInput
                  type="number"
                  name="nb_pieces_principales"
                  placeholder="3"
                  unit={"pièces"}
                  className={styles.grow}
                  marginForm=""
                />
                <TextInput
                  type="number"
                  name="surface_total"
                  placeholder="45"
                  step={"0.01"}
                  unit={"m²"}
                  className={styles.grow}
                  marginForm=""
                />
                {isColocIndividuelle && (
                  <TextInput
                    type="number"
                    name="surface_chambre"
                    placeholder="12"
                    step={"0.01"}
                    unit={"m²"}
                    label={"Surface chambre"}
                    className={styles.grow}
                    marginForm=""
                  />
                )}
              </div>
              {isColocIndividuelle && (
                <Callout variant="warning">
                  Dans le cadre d'une colocation avec baux individuels, vous
                  pouvez indiquer uniquement le nombre de pièces à usage
                  privatif du colocataire, et leur surface totale.
                </Callout>
              )}
            </div>
          }
          label="Nombre de pièces et surface totale en m²"
          icon={<Surface />}
          mandatory
          className={styles.grow}
          marginForm=""
        />
      )}
      {values.type === "habitation" && (
        <StyledForm
          formElement={
            <div className={styles.gridCols}>
              <Radio
                name="type_propriete"
                value="monopropriete"
                label="Monopropriete"
                dependon={"id_parent"}
              />
              <Radio
                name="type_propriete"
                value="copropriete"
                label="Copropriete"
                dependon={"id_parent"}
              />
            </div>
          }
          label="Type de propriété"
          icon={<ImmeublePeople />}
          mandatory
        />
      )}
      {values.type_habitat === "collectif" && (
        <>
          <StyledForm
            formElement={
              <div className={styles.gridCols}>
                <Radio
                  name="eau_chaude"
                  value="individuel"
                  label="Eau chaude individuelle"
                  dependon={"id_parent"}
                />
                <Radio
                  name="eau_chaude"
                  value="collectif"
                  label="Eau chaude collective"
                  dependon={"id_parent"}
                />
              </div>
            }
            label="Eau chaude"
            icon={<Water />}
            mandatory
          />
          <StyledForm
            formElement={
              <div className={styles.gridCols}>
                <Radio
                  name="chauffage"
                  value="individuel"
                  label="Chauffage individuel"
                  dependon={"id_parent"}
                />
                <Radio
                  name="chauffage"
                  value="collectif"
                  label="Chauffage collectif"
                  dependon={"id_parent"}
                />
              </div>
            }
            label="Type de chauffage"
            icon={<Temperature />}
            mandatory
          />
        </>
      )}
      {values.type === "commercial" && descriptionBailField}
      {(values.type === "habitation" || values.type === "commercial") && (
        <Card type={"blue"} padding={"sm"}>
          <Title as="h3" variant="deca" weight="bold">
            Informations optionnelles
          </Title>
          <Text>
            Tous les champs ci-dessous sont facultatifs : vous pourrez les
            compléter ou les modifier plus tard depuis la fiche du bien.
          </Text>
        </Card>
      )}
      {values.type !== "garage" && (
        <StyledForm
          formElement={
            <ExpandableCheckboxList
              name="autres_pieces"
              items={
                values.type === "habitation"
                  ? autresPiecesCheckboxItems
                  : localPiecesCheckboxItems
              }
              addItem={addItem}
              className={styles.checkboxList}
            />
          }
          label={`Composition du ${
            values.type === "habitation" ? "logement" : "local"
          }`}
          icon={<HousePlus stroke="#707989" />}
        />
      )}
      {values.type === "habitation" && (
        <>
          <StyledForm
            formElement={
              <ExpandableCheckboxList
                name="equipements"
                items={equipementsCheckboxItems}
                addItem={addItem}
                className={styles.checkboxList}
              />
            }
            label="Equipements du logement"
            icon={<Cctv />}
          />
          <StyledForm
            formElement={
              <ExpandableCheckboxList
                name="technologies"
                items={technologiesCheckboxItems}
                addItem={addItem}
                className={styles.checkboxList}
              />
            }
            label="Internet"
            icon={<Internet />}
          />
          <StyledForm
            formElement={
              <div className={styles.row}>
                <TextInput
                  type="number"
                  name="depenses_energetiques_min"
                  placeholder="900"
                  step={"0.01"}
                  unit={"€"}
                  className={styles.grow}
                />
                <TextInput
                  type="number"
                  name="depenses_energetiques_max"
                  placeholder="1100"
                  step={"0.01"}
                  unit={"€"}
                  className={styles.grow}
                />
              </div>
            }
            label={
              <div>
                Montant annuel des dépenses énergetiques minimum et maximum{" "}
                <Tooltip
                  content={
                    <Text>
                      La fourchette de dépense à indiquer figure en page 1 de
                      votre DPE dans la rubrique "Estimation des coûts annuels
                      d'énergie du logement"
                      <img
                        src={"/photo_dpe.png"}
                        alt={"exemple de dpe"}
                        width={250}
                        className={styles.dpeImg}
                      />
                    </Text>
                  }
                >
                  <IconInfoCircle size={16} />
                </Tooltip>
              </div>
            }
            icon={<Charges />}
          />
          <StyledForm
            formElement={
              <Select
                name="indice_dpe"
                dependon="id_parent"
                label={"Indice DPE"}
              >
                <option value={""}>Choisissez un indice</option>
                <option value={"A"}>A</option>
                <option value={"B"}>B</option>
                <option value={"C"}>C</option>
                <option value={"D"}>D</option>
                <option value={"E"}>E</option>
                <option value={"F"}>F</option>
                <option value={"G"}>G</option>
              </Select>
            }
            label={"DPE"}
            icon={<Bank stroke={"#9D9D9D"} />}
          />
          <StyledForm
            formElement={
              <TextInput
                type="text"
                inputMode="numeric"
                name="year_built"
                dependon="id_parent"
                placeholder="ex. 1972"
                subText={
                  values.year_built && !/^\d{4}$/.test(values.year_built)
                    ? `Période enregistrée : ${values.year_built}. Précisez l'année pour affiner les diagnostics attendus.`
                    : "Détermine les diagnostics à fournir : plomb (avant 1949), amiante (avant juillet 1997), électricité et gaz (plus de 15 ans)."
                }
              />
            }
            label="Année de construction"
            icon={<CalendarCheck stroke={"#9D9D9D"} />}
          />
        </>
      )}
      {values.type_habitat === "collectif" && (
        <>
          <StyledForm
            formElement={
              <ExpandableCheckboxList
                name="locaux_privatifs"
                items={locauxPrivatifsCheckboxItems}
                addItem={addItem}
                className={styles.checkboxList}
              />
            }
            label="Parties de l'IMMEUBLE à usage privatif"
            icon={<HousePrivatif />}
          />
          <StyledForm
            formElement={
              <ExpandableCheckboxList
                name="locaux_communs"
                items={locauxCommunsCheckboxItems}
                addItem={addItem}
                className={styles.checkboxList}
              />
            }
            label="Parties de l'IMMEUBLE à usage commun"
            icon={<HouseTree />}
          />
        </>
      )}
      {values.is_coloc && (
        <>
          <StyledForm
            formElement={
              <ExpandableCheckboxList
                name="logement_espaces_communs"
                items={logementEspacesCommunsCheckboxItems}
                addItem={addItem}
                className={styles.checkboxList}
              />
            }
            label="Si colocation, parties du LOGEMENT à usage commun"
            icon={<HouseTree />}
          />
          <StyledForm
            formElement={
              <ExpandableCheckboxList
                name="logement_espaces_privatifs"
                items={logementEspacesPrivatifsCheckboxItems}
                addItem={addItem}
                className={styles.checkboxList}
              />
            }
            label="Si colocation, parties du LOGEMENT à usage privée"
            icon={<HouseTree />}
          />
        </>
      )}
      <StyledForm
        formElement={
          <TextInput type="date" name="date_achat" dependon="id_parent" />
        }
        label={
          <div>
            Date d'achat{" "}
            <Tooltip
              content={
                "Renseigner cette date permet de ne pas générer les loyers des locataires existants que vous ajouterez, avant la date d'achat."
              }
            >
              <IconInfoCircle size={16} />
            </Tooltip>
          </div>
        }
        icon={<CalendarCheck stroke={"#9D9D9D"} />}
      />
      {!isCreation && (
        <StyledForm
          formElement={
            <TextInput
              type="text"
              name="prix_achat"
              dependon="id_parent"
              unit={"€"}
            />
          }
          label={
            <div>
              Prix d'achat{" "}
              <Tooltip
                content={
                  "Nécessaire si vous prenez l'offre comptable avec nous. Si immeuble en monopropriété, vous pouvez mettre le prix sur l'immeuble."
                }
              >
                <IconInfoCircle size={16} />
              </Tooltip>
            </div>
          }
          icon={<Bank stroke={"#9D9D9D"} />}
        />
      )}
      {!isCreation && (
        <StyledForm
          formElement={<SelectIban />}
          label={
            <div>
              IBAN{" "}
              <Tooltip
                content={
                  "L'IBAN sera intégré au bail, et envoyé par mail au locataire lors du premier contact. Veuillez vérifier que l'IBAN est correct."
                }
              >
                <IconInfoCircle size={16} />
              </Tooltip>
            </div>
          }
          icon={<Bank stroke={"#9D9D9D"} />}
        />
      )}
      <StyledForm
        formElement={
          <TextInput
            type="text"
            name="identifiant_fiscal"
            dependon="id_parent"
          />
        }
        label={
          <div>
            Identifiant fiscal du logement{" "}
            <Tooltip
              content={
                "Mention obligatoire sur les contrats de location depuis le 1er janvier 2024 (hors baux mobilité, colocations et locaux professionnels). Vous retrouvez le numéro fiscal de votre logement depuis le site Impot.gouv.fr rubrique “Biens immobiliers” puis en cliquant sur “Consulter” pour le bien concerné."
              }
            >
              <IconInfoCircle size={16} />
            </Tooltip>
          </div>
        }
        icon={<Bank stroke={"#9D9D9D"} />}
      />
      <StyledForm
        formElement={
          <Checkbox
            name={"gli"}
            className={styles.checkbox}
            dependon={"id_bien"}
          >
            Besoin de prendre une GLI sur ce bien
          </Checkbox>
        }
        label={
          <div>
            GLI{" "}
            <Tooltip
              direction={"right"}
              content={
                "Il s'agit juste d'un indicateur, permettant de pouvoir filtrer sur les biens à couvrir par une assurance loyers impayés (GLI)."
              }
            >
              <IconInfoCircle size={16} />
            </Tooltip>
          </div>
        }
        icon={<Bank stroke={"#9D9D9D"} />}
      />

      {values.type !== "garage" && (
        <StyledForm
          formElement={
            <div className={styles.row}>
              <TextInput
                type="text"
                name="compteur_elec"
                dependon="id_parent"
                label={"Électricité"}
              />
              <TextInput
                type="text"
                name="compteur_gaz"
                dependon="id_parent"
                label={"Gaz"}
              />
              <TextInput
                type="text"
                name="compteur_eau"
                dependon="id_parent"
                label={"Eau"}
              />
              <TextInput
                type="text"
                name="compteur_fibre"
                dependon="id_parent"
                label={"Fibre"}
              />
            </div>
          }
          label={"Numéros de compteur"}
          icon={<Bank stroke={"#9D9D9D"} />}
        />
      )}
      {values.type !== "commercial" && descriptionBailField}
      <StyledForm
        formElement={
          <TextArea
            type="text"
            name="description_public"
            placeholder="Superbe appartement en plein centre de Lyon, vous trouverez ..."
            rows={"5"}
          />
        }
        label={
          <div>
            Description publique du bien{" "}
            <Tooltip
              content={
                "Vous pouvez par exemple mettre ici le contenu de votre annonce. Ce texte sera visible par les candidats lorsqu'ils soumettrons leur dossier."
              }
            >
              <IconInfoCircle size={16} />
            </Tooltip>
          </div>
        }
        icon={<DescriptionLogement />}
      />
      {!isCreation && (
        <StyledForm
          formElement={<ClausesForm leaseType={leaseTemplate?.type} />}
          label="Article spécifique à faire paraître dans le bail"
          icon={<ConditionsParticulieres />}
        />
      )}
    </div>
  );
}
